"use client";

import "@/lib/i18n";
import i18n from "@/lib/i18n";

import {
  ChatModelRunOptions,
  ChatModelRunResult,
  type ChatModelAdapter,
  useAuiState,
} from "@assistant-ui/react";

import { ThreadListSidebar } from "@/components/assistant-ui/threadlist-sidebar";
import { ChatsListPage } from "@/components/chats/chats-list-page";
import { ErrorBoundary } from "react-error-boundary";
import { Thread } from "@/components/assistant-ui/thread";
import {
  CreateChatModelAdapterDeps,
  DocumentCitationData,
  readJsonSse,
  safeJsonParse,
  streamChatToRunResults,
} from "@/components/custom/chatModelAdapters";
import {
  abortAllInFlightDocFetches,
  openDocumentInSidebar,
  openGeneratedDocumentInSidebar,
} from "@/lib/threadDocumentOps";
import { ThreadNotFoundError } from "@/components/custom/providers/threadHistoryProvider";
import { apiAuthFetch } from "@/lib/authFetch";
import CustomThreadListProvider from "@/components/custom/providers/customThreadListProvider";
import RouteWatcher from "@/components/custom/routeWatcher";
import { PromptAutoSend } from "@/components/custom/promptAutoSend";
import { QuotaExceededDialog } from "@/components/custom/quotaExceededDialog";
import { appEvents } from "@/lib/events";
import { ContextTrimmedToastListener } from "@/components/custom/contextTrimmedToast";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  RouteContextValues,
  useRouteContext,
} from "@/components/custom/contexts/routeContext";
import { useMcpServers } from "@/components/custom/contexts/mcpServersContext";
import { useScopePolicy } from "@/components/custom/contexts/scopePolicyContext";
import { useThreadListContext } from "@/components/custom/contexts/threadContext";
import { useExtendedThinking } from "@/components/custom/contexts/extendedThinkingContext";
import { useWebSearch } from "@/components/custom/contexts/webSearchContext";
import { useWebSearchDomains } from "@/components/custom/contexts/webSearchDomainsContext";
import { useMcpServerDateFilters } from "@/components/custom/contexts/mcpServerDateFiltersContext";
import { useAiProvider } from "@/components/custom/contexts/aiProviderContext";
import { AppSettingsProvider } from "@/components/custom/providers/appSettingsProvider";
import { ChatSettingsProvider } from "@/components/custom/providers/chatSettingsProvider";
import { useTranslation } from "react-i18next";
import { SettingsShell } from "@/components/custom/settings/settings-shell";
import { usePdfDocumentStore } from "@/components/pdf/pdfDocumentStore";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { useOnMainThreadSwitched } from "@/hooks/use-on-main-thread-switched";
import { findUnstableEntry } from "@/lib/assistantUiTypes";
import { installGlobalErrorHandlers } from "@/lib/globalErrorHandlers";
import { ClientLogger } from "@/lib/clientLogger";
import { WebViewBackButton } from "@/components/custom/webviewBackButton";
import { useAnonymizationStore } from "@/components/custom/stores/anonymizationStore";
import { useAnonymizationSendGate } from "@/components/custom/stores/anonymizationSendGate";
import { StreamVisibilityManager } from "@/lib/streamVisibilityManager";

import { SettingsIcon, SearchIcon } from "lucide-react";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { SearchPalette } from "@/components/assistant-ui/search-palette";
import { PdfSidebarPanel } from "@/components/pdf/PdfSidebarPanel";
import { Link } from "react-router-dom";

function ChatErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}) {
  const { t } = useTranslation();

  if (error instanceof ThreadNotFoundError) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="space-y-2 text-center">
          <h1 className="font-semibold text-xl">{t("thread.notFound")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("thread.notFoundDescription")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="max-w-md space-y-3 text-center">
        <h2 className="font-semibold text-xl">{t("error.boundaryFallback")}</h2>
        <details className="text-left text-muted-foreground text-xs">
          <summary className="cursor-pointer">
            {t("error.detailsToggle")}
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words">
            {((error as Error).message ?? "Unknown error").slice(0, 500)}
          </pre>
        </details>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm hover:bg-primary/90"
          onClick={resetErrorBoundary}
        >
          {t("error.tryAgain")}
        </button>
      </div>
    </div>
  );
}

// Mirrors the live (URL-derived) thread id so module-level callbacks invoked
// from in-flight SSE streams can detect they're firing for a previous thread.
// Updated by AssistantInner via a useEffect on routeContext.threadId.
let _liveThreadId: string | undefined;
export const getLiveThreadId = (): string | undefined => _liveThreadId;

// Import for local use (avoids circular deps).
import {
  getAuiRemoteThreadId,
  setAuiRemoteThreadId,
} from "@/lib/auiThreadIdBridge";

export async function handleCancel(
  threadId: string | undefined,
): Promise<void> {
  if (!threadId) return;

  const response = await apiAuthFetch(`api/chat/${threadId}/cancel`, {
    method: "PATCH",
  });

  if (!response.ok) {
    ClientLogger.warn("Failed to cancel thread", {
      component: "handleCancel",
      metadata: { threadId, status: response.status },
    });
  }
}

export const Assistant = memo(function Assistant() {
  return (
    <AppSettingsProvider>
      <ChatSettingsProvider>
        <AssistantInner />
      </ChatSettingsProvider>
    </AppSettingsProvider>
  );
});

const AssistantInner = memo(function AssistantInner() {
  const routeContext = useRouteContext();
  const { selectedServers } = useMcpServers();
  const { extendedThinking } = useExtendedThinking();
  const { webSearch } = useWebSearch();
  const {
    limitToRecommended: webSearchLimitToRecommended,
    recommendedSources,
  } = useWebSearchDomains();
  const { selectedProvider } = useAiProvider();
  const { setThreadId: setScopePolicyThreadId, hydrateFromThread } =
    useScopePolicy();
  const { threadItems } = useThreadListContext();

  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);

  // Global ⌘K / Ctrl+K — registered here so it works even when the sidebar
  // is hidden (showThreadList=false / webview layout).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Install global error handlers once
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);

  // Ref so the memoized adapter always reads the latest routeContext at
  // call-time without needing to be recreated on every context change.
  const routeContextRef = useRef(routeContext);
  useEffect(() => {
    routeContextRef.current = routeContext;
  }, [routeContext]);

  // Mirror routeContext.threadId into module-scoped _liveThreadId so
  // module-level callbacks (onDocumentCitation, openDocumentInSidebar)
  // invoked from streaming or async resolution can detect stale events.
  // Also notify StreamVisibilityManager to abort background streams.
  useEffect(() => {
    _liveThreadId = routeContext.threadId;
    StreamVisibilityManager.activate(routeContext.threadId);
  }, [routeContext.threadId]);

  const selectedMcpServersRef = useRef(selectedServers);
  useEffect(() => {
    selectedMcpServersRef.current = selectedServers;
  }, [selectedServers]);

  const extendedThinkingRef = useRef(extendedThinking);
  useEffect(() => {
    extendedThinkingRef.current = extendedThinking;
  }, [extendedThinking]);

  const webSearchRef = useRef(webSearch);
  useEffect(() => {
    webSearchRef.current = webSearch;
  }, [webSearch]);

  const webSearchLimitToRecommendedRef = useRef(webSearchLimitToRecommended);
  useEffect(() => {
    webSearchLimitToRecommendedRef.current = webSearchLimitToRecommended;
  }, [webSearchLimitToRecommended]);

  const recommendedSourcesRef = useRef(recommendedSources);
  useEffect(() => {
    recommendedSourcesRef.current = recommendedSources;
  }, [recommendedSources]);

  const selectedProviderRef = useRef(selectedProvider);
  useEffect(() => {
    selectedProviderRef.current = selectedProvider;
  }, [selectedProvider]);

  // Keep the scope context's per-thread persistence in sync with the active thread
  // so a thread switch loads the right basePolicy + accumulated approvals from
  // localStorage. Mirrors how McpServersContext.setThreadId is wired.
  useEffect(() => {
    setScopePolicyThreadId(routeContext.threadId ?? undefined);
  }, [routeContext.threadId, setScopePolicyThreadId]);

  // Hydrate scope context from the thread's backend scope fields once available.
  // threadItems are populated by the list adapter's list() and fetch() calls.
  useEffect(() => {
    const tid = routeContext.threadId;
    if (!tid) return;
    const item = threadItems.find((t) => t.remoteId === tid);
    if (!item) return;
    hydrateFromThread({
      mainCaseId: item.mainCaseId,
      additionalCaseIds: item.additionalCaseIds,
      additionalDocumentsByCase: item.additionalDocumentsByCase,
    });
  }, [routeContext.threadId, threadItems, hydrateFromThread]);

  // Memoized: stable adapter across renders.
  // apiUrl is a build-time env constant; readJsonSse/safeJsonParse are module-
  // level imports. routeContextRef is a stable useRef — not a dependency.
  const modelAdapter = useMemo<ChatModelAdapter>(
    () =>
      createChatModelAdapter(
        { readJsonSse, safeJsonParse },
        routeContextRef,
        selectedMcpServersRef,
        extendedThinkingRef,
        webSearchRef,
        webSearchLimitToRecommendedRef,
        recommendedSourcesRef,
        selectedProviderRef,
      ),
    [],
  );

  console.log("Assistant initialized with routeState:", routeContext);

  return (
    <ErrorBoundary
      FallbackComponent={ChatErrorFallback}
      onError={(error, info) => {
        // componentStack is only exposed by react-error-boundary's onError;
        // it is NOT available inside the FallbackComponent. Logging here also
        // guarantees a single emit per boundary hit (vs. one per fallback render).
        ClientLogger.error("React error boundary hit", {
          error: error instanceof Error ? error : undefined,
          component: "ChatErrorFallback",
          metadata: { componentStack: info.componentStack ?? null },
        });
      }}
    >
      <CustomThreadListProvider modelAdapter={modelAdapter}>
        <RouteWatcher />
        <PromptAutoSend />
        <McpThreadIdSync />
        <GeneratedDocStoreRehydrator />
        <QuotaExceededDialog />
        <ContextTrimmedToastListener />
        <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} />
        {routeContext.settingsView && (
          <div className="flex h-dvh w-full pr-0.5">
            <ThreadListSidebar onOpenSearch={openSearch} />
            <SettingsShell
              view={routeContext.settingsView}
              skillId={routeContext.settingsSkillId}
            />
            <PdfSidebarPanel />
          </div>
        )}
        {!routeContext.settingsView &&
          routeContext.showThreadList === false && (
            <div className="flex h-dvh w-full flex-col">
              <header className="flex h-10 shrink-0 items-center justify-between gap-2 border-b px-4">
                <div className="flex items-center">
                  <WebViewBackButton mode="header" />
                </div>
                <div className="flex items-center gap-2">
                  <SearchButton onOpen={openSearch} />
                  <SettingsNavButton />
                </div>
              </header>
              <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 overflow-hidden">
                  <OptimizedThread />
                </div>
                <PdfSidebarPanel />
              </div>
            </div>
          )}
        {!routeContext.settingsView && routeContext.showThreadList === true && (
          <div className="flex h-dvh w-full pr-0.5">
            <ThreadListSidebar onOpenSearch={openSearch} />
            <div className="flex h-full flex-1 overflow-hidden">
              <div className="flex-1 overflow-hidden">
                {routeContext.chatsView ? (
                  <ChatsListPage />
                ) : (
                  <OptimizedThread />
                )}
              </div>
              {!routeContext.chatsView && <PdfSidebarPanel />}
            </div>
          </div>
        )}
      </CustomThreadListProvider>
    </ErrorBoundary>
  );
});

const THREAD_LOADING_ROOT_STYLE: CSSProperties = {
  position: "relative",
  height: "100%",
};

const THREAD_LOADING_VISIBLE_STYLE: CSSProperties = {
  height: "100%",
};

const THREAD_LOADING_HIDDEN_STYLE: CSSProperties = {
  height: "100%",
  visibility: "hidden",
};

const THREAD_LOADING_HELMET_FRAME_STYLE: CSSProperties = {
  pointerEvents: "none",
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity 300ms ease",
};

const THREAD_LOADING_HELMET_FRAME_VISIBLE_STYLE: CSSProperties = {
  ...THREAD_LOADING_HELMET_FRAME_STYLE,
  opacity: 1,
};

const THREAD_LOADING_HELMET_FRAME_HIDDEN_STYLE: CSSProperties = {
  ...THREAD_LOADING_HELMET_FRAME_STYLE,
  opacity: 0,
};

const THREAD_LOADING_SKELETON_STYLE: CSSProperties = {
  width: "clamp(18rem, 32vw, 30rem)",
  maxWidth: "70vw",
  maxHeight: "70vh",
  opacity: 0.06,
};

function OptimizedThread() {
  const routeState = useRouteContext();
  const isLoading = useAuiState((s) => s.thread.isLoading);
  const messageCount = useAuiState((s) => s.thread.messages.length);
  const [showHelmet, setShowHelmet] = useState(false);

  // Belt-and-suspenders clear: threadHistoryProvider's load() finally only runs
  // once per runtime instance (assistant-ui caches runtimes per thread), so a
  // cached-thread revisit would otherwise leave loadingThreadFromUrl stuck on.
  // Once AUI reports the thread is no longer loading, clear the flag — the
  // chat is rendered underneath the helmet at that point.
  useEffect(() => {
    if (routeState.loadingThreadFromUrl && !isLoading) {
      routeState.onRouteStateChanged({ loadingThreadFromUrl: false });
    }
  }, [routeState, isLoading]);

  // Safety timeout: if loadingThreadFromUrl stays true for 5s (stale state,
  // cached runtime that never fires isLoading transition), force-clear it.
  useEffect(() => {
    if (!routeState.loadingThreadFromUrl) return;
    const timer = setTimeout(() => {
      routeState.onRouteStateChanged({ loadingThreadFromUrl: false });
    }, 5000);
    return () => clearTimeout(timer);
  }, [routeState, routeState.loadingThreadFromUrl]);

  // Debounce helmet visibility: only show after 200ms of continuous loading.
  // For cached/fast thread switches (< 200ms), the helmet never appears → no blink.
  const wantsHelmet =
    routeState.loadingThreadFromUrl && isLoading && messageCount === 0;
  useEffect(() => {
    if (!wantsHelmet) {
      setShowHelmet(false);
      return;
    }
    const timer = setTimeout(() => setShowHelmet(true), 200);
    return () => clearTimeout(timer);
  }, [wantsHelmet]);

  return (
    <div className="relative h-full" style={THREAD_LOADING_ROOT_STYLE}>
      <ThreadLoadingHelmet visible={showHelmet} />
      <div
        className={showHelmet ? "invisible h-full" : "h-full"}
        style={
          showHelmet
            ? THREAD_LOADING_HIDDEN_STYLE
            : THREAD_LOADING_VISIBLE_STYLE
        }
      >
        <Thread />
      </div>
    </div>
  );
}

function ThreadLoadingHelmet({ visible }: { visible: boolean }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
      style={
        visible
          ? THREAD_LOADING_HELMET_FRAME_VISIBLE_STYLE
          : THREAD_LOADING_HELMET_FRAME_HIDDEN_STYLE
      }
      aria-hidden
    >
      <div
        className="flex flex-col gap-4 animate-[helmet-pulse_3s_ease-in-out_infinite] rounded-xl bg-accent"
        style={THREAD_LOADING_SKELETON_STYLE}
      >
        <div className="h-4 w-3/4 rounded-md bg-current opacity-40" />
        <div className="h-4 w-full rounded-md bg-current opacity-30" />
        <div className="h-4 w-5/6 rounded-md bg-current opacity-30" />
        <div className="h-4 w-2/3 rounded-md bg-current opacity-20" />
      </div>
    </div>
  );
}

function SettingsNavButton() {
  const { t } = useTranslation();
  return (
    <Link to="/settings">
      <TooltipIconButton
        tooltip={t("sidebar.settings")}
        variant="ghost"
        className="size-7 cursor-pointer"
      >
        <SettingsIcon className="text-muted-foreground" />
      </TooltipIconButton>
    </Link>
  );
}

function SearchButton({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <TooltipIconButton
      tooltip={t("sidebar.search")}
      variant="ghost"
      className="size-7 cursor-pointer"
      onClick={onOpen}
    >
      <SearchIcon className="text-muted-foreground" />
    </TooltipIconButton>
  );
}

function McpThreadIdSync() {
  const { setThreadId: setMcpThreadId } = useMcpServers();
  const { setThreadId: setExtendedThinkingThreadId } = useExtendedThinking();
  const { setThreadId: setWebSearchThreadId } = useWebSearch();
  const { setThreadId: setWebSearchDomainsThreadId } = useWebSearchDomains();
  const { setThreadId: setMcpDateFiltersThreadId } = useMcpServerDateFilters();
  useOnMainThreadSwitched(
    useCallback(
      (nextId: string, prevId: string | undefined) => {
        // Pairs with the [citation] / [doc-sidebar] stale-event log lines so
        // DevTools shows the full transition story when something goes wrong.
        if (prevId !== undefined) {
          console.debug("[thread-switch]", prevId, "->", nextId);
        }
        setMcpThreadId(nextId);
        setExtendedThinkingThreadId(nextId);
        setWebSearchThreadId(nextId);
        setWebSearchDomainsThreadId(nextId);
        setMcpDateFiltersThreadId(nextId);
        // Order matters: abort first so any in-flight fetch's late resolution
        // can't race with closeAllDocuments and re-add itself to the store.
        abortAllInFlightDocFetches();
        usePdfDocumentStore.getState().closeAllDocuments();
        // ADR-021: clear the per-thread generated-document store too. Without this, an SSE
        // data-document-* event from a stale stream could repopulate the new thread's view
        // with the old thread's documents — same shape as the cross-thread parentId leak.
        useGeneratedDocumentStore.getState().clear();
      },
      [
        setMcpThreadId,
        setExtendedThinkingThreadId,
        setWebSearchThreadId,
        setWebSearchDomainsThreadId,
        setMcpDateFiltersThreadId,
      ],
    ),
    { waitForLoaded: false },
  );
  return null;
}

/**
 * Repopulates generatedDocumentStore after a thread switch clears it (ADR-021).
 *
 * Problem: McpThreadIdSync calls clear() on every thread switch (to prevent stale
 * SSE events from a previous stream leaking into the new view). For fresh loads,
 * threadHistoryProvider.load() calls fetchDocuments and the store is populated
 * before messages render. But load() is guarded and only runs once per runtime
 * instance — on returning to a cached thread, clear() empties the store and nothing
 * re-fills it, leaving DocumentBlock stuck showing the "generating" skeleton.
 *
 * Fix: after the new thread's messages are available in AUI state, read all
 * generated-document IDs from the message parts and call fetchDocuments so
 * the store is re-populated. Rendering here after McpThreadIdSync in the tree
 * ensures the clear() effect always fires before this rehydration effect.
 */
function GeneratedDocStoreRehydrator() {
  const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
  const isLoading = useAuiState((s) => s.thread.isLoading);
  const messages = useAuiState((s) => s.thread.messages);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const prevThreadIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // First render: initialize prevRef and bail.
    if (prevThreadIdRef.current === undefined) {
      prevThreadIdRef.current = mainThreadId;
      return;
    }

    // Not a thread switch.
    if (!mainThreadId || mainThreadId === prevThreadIdRef.current) return;

    // For fresh loads: defer until AUI has loaded the thread's messages.
    if (isLoading) return;

    prevThreadIdRef.current = mainThreadId;

    const allDocIds: string[] = [];
    for (const msg of messagesRef.current) {
      if (msg.role !== "assistant") continue;
      const entry = findUnstableEntry(
        msg.metadata?.unstable_data,
        "generated-documents",
      );
      if (entry?.ids.length) allDocIds.push(...entry.ids);
    }

    if (allDocIds.length > 0) {
      void useGeneratedDocumentStore
        .getState()
        .fetchDocuments(allDocIds)
        .catch(() => {
          /* handled internally */
        });
    }
  }, [mainThreadId, isLoading]);

  return null;
}

export const navigateToCitation = (
  citation: DocumentCitationData,
  allCitations?: DocumentCitationData[],
  originatingThreadId?: string,
) => {
  // Drop clicks whose React tree belongs to a thread the user has already
  // left. Defends against races where a click fires just before the
  // thread-switch hook tears the panel down.
  if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
    console.debug(
      "[citation-nav] dropping stale click from",
      originatingThreadId,
      "(now on",
      getLiveThreadId(),
      ")",
    );
    return;
  }
  const page = citation.pages
    ? parseInt(citation.pages.split("-")[0], 10)
    : undefined;
  const store = usePdfDocumentStore.getState();
  const existingDoc = store.documents.find((doc) => doc.id === citation.docId);
  console.debug("[citation-nav] navigateToCitation:", {
    docId: citation.docId,
    pages: citation.pages,
    snippet: citation.snippet?.slice(0, 60),
    docExists: !!existingDoc,
  });

  // Bulk-add all citations for this document so the sidebar shows the full list.
  const relatedCitations =
    allCitations?.filter((c) => c.docId === citation.docId) ?? [];
  for (const c of relatedCitations) {
    if (c.snippet) {
      const p = c.pages ? parseInt(c.pages.split("-")[0], 10) : undefined;
      store.addCitationSnippet(c.docId, c.snippet, p, false);
    }
  }

  // Add/activate the clicked snippet.
  if (citation.snippet) {
    store.addCitationSnippet(citation.docId, citation.snippet, page);
  }

  if (existingDoc) {
    // Doc already open — switch to it and activate the citation.
    store.setActiveDocument(citation.docId, citation.snippet);
    if (page) {
      store.setPendingNavigation(citation.docId, { page });
    }
    store.setPanelOpen(true);
    return;
  }

  // Doc not open — open + navigate to cited page.
  openDocumentInSidebar(citation.docId, page, originatingThreadId);
};

export const onDocumentCitation = (
  citation: DocumentCitationData,
  originatingThreadId: string | undefined,
) => {
  // Drop events that came from a stream belonging to a thread the user
  // has already left. assistant-ui does not abort the previous thread's
  // run on switch, so the producer keeps draining the SSE stream and
  // firing this callback indefinitely.
  if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
    console.debug(
      "[citation] dropping stale citation from",
      originatingThreadId,
      "(now on",
      getLiveThreadId(),
      ")",
    );
    return;
  }
  const page = citation.pages
    ? parseInt(citation.pages.split("-")[0], 10)
    : undefined;
  const store = usePdfDocumentStore.getState();

  // Store the snippet for later highlighting (don't activate during streaming).
  if (citation.snippet) {
    store.addCitationSnippet(citation.docId, citation.snippet, page, false);
  }
};

/**
 * Phase 2 / C1: auto-open the generated document in the PDF sidebar when the
 * backend signals data-document-ready. Same staleness gate as onDocumentCitation:
 * if the run's originating thread has been left, drop the event silently.
 *
 * The store's mode-switch logic in openDocument handles the wipe semantics —
 * any open citation tabs are evicted by design when a generated doc takes over.
 */
const onDocumentReady = (
  documentId: string,
  title: string | undefined,
  originatingThreadId: string | undefined,
) => {
  if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
    console.debug(
      "[doc-sidebar] dropping stale generated-doc-ready",
      documentId,
      "from",
      originatingThreadId,
      "(now on",
      getLiveThreadId(),
      ")",
    );
    return;
  }
  void openGeneratedDocumentInSidebar(documentId, title, originatingThreadId);
};

// Wait until AUI exposes a real (non-__LOCAL) remoteId for the active thread.
// AUI publishes the remoteId synchronously when adapter.initialize()'s `then`
// callback runs (see RemoteThreadListThreadListRuntimeCore.initialize). We
// read it from the module-scoped getter that AuiThreadIdBridge keeps in sync
// — that bridge subscribes directly to AUI's store, so the value is fresh
// the moment AUI emits it, with no detour through routeContext + React
// re-render. Falls back to routeContext for the first-render edge case where
// the bridge hasn't yet flushed its initial value.
function waitForInitializedThreadId(
  routeContextRef: { current: RouteContextValues },
  abortSignal: AbortSignal,
  timeoutMs: number,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      abortSignal.removeEventListener("abort", onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const check = () => {
      if (abortSignal.aborted) {
        onAbort();
        return;
      }
      const auiId = getAuiRemoteThreadId();
      const routeId = routeContextRef.current.threadId;
      const id = auiId && !auiId.startsWith("__LOCAL") ? auiId : routeId;
      if (id && !id.startsWith("__LOCAL")) {
        cleanup();
        resolve(id);
        return;
      }
      if (Date.now() > deadline) {
        cleanup();
        reject(
          new Error(`Thread initialization timed out after ${timeoutMs}ms`),
        );
        return;
      }
      // Short interval — the bridge updates the module-scoped value
      // synchronously on every AUI store notification, so most waits resolve
      // on the first tick after the initialize POST returns.
      timer = setTimeout(check, 25);
    };

    abortSignal.addEventListener("abort", onAbort);
    check();
  });
}

function createChatModelAdapter(
  deps: CreateChatModelAdapterDeps,
  routeContextRef: { current: RouteContextValues },
  selectedMcpServersRef: { current: string[] },
  extendedThinkingRef: { current: boolean },
  webSearchRef: { current: boolean },
  webSearchLimitToRecommendedRef: { current: boolean },
  recommendedSourcesRef: { current: string[] },
  selectedProviderRef: { current: string | null },
): ChatModelAdapter {
  return {
    async *run(
      options: ChatModelRunOptions,
    ): AsyncGenerator<ChatModelRunResult, void, unknown> {
      const {
        messages,
        abortSignal,
        unstable_threadId,
        unstable_assistantMessageId,
      } = options;

      // Defensive threadId resolution: unstable_threadId can be undefined during the
      // race window between initialize() resolving a remoteId and useLocalRuntime's
      // internal state subscription flushing. Fall back to routeContext (URL-derived
      // mirror of the active thread), and if still missing, wait for it to become
      // available rather than failing the user's send. See docs/adr/001.
      const routeThreadId = routeContextRef.current.threadId;
      let resolvedThreadId = unstable_threadId ?? routeThreadId;

      if (!resolvedThreadId || resolvedThreadId.startsWith("__LOCAL")) {
        console.info(
          "[chat-adapter] threadId not yet initialized; waiting up to 10s",
          {
            unstable_threadId,
            routeThreadId,
            messageCount: messages.length,
          },
        );
        resolvedThreadId = await waitForInitializedThreadId(
          routeContextRef,
          abortSignal,
          10_000,
        );
        console.info("[chat-adapter] threadId resolved after wait", {
          resolvedThreadId,
        });
      } else if (
        unstable_threadId &&
        routeThreadId &&
        unstable_threadId !== routeThreadId
      ) {
        ClientLogger.warn("threadId drift; preferring runtime source", {
          component: "chatModelAdapter",
          metadata: { unstable_threadId, routeThreadId },
        });
      }

      // Anonymisation send-warning gate. Fires when the user is about to send a
      // turn with anonymisation explicitly OFF on a thread that previously had
      // at least one successful anonymised turn — mid-thread switch from
      // anonymised history to plaintext mixes the two for the LLM's next turn,
      // including any new PII the user just typed. Placing the gate here (in
      // the adapter rather than on the Send button) catches every path uniformly:
      // mouse click, Enter key, programmatic `composer.send()`. The await is the
      // entire reason for the modal's existence — on confirm we record a
      // per-thread acknowledgment (localStorage-backed) so the modal does not
      // re-fire on subsequent sends; on cancel we throw an `AbortError` to
      // unwind the run the same way assistant-ui handles user-initiated
      // cancellation (no error toast, composer state intact, next click can
      // retry).
      if (resolvedThreadId) {
        const anonStore = useAnonymizationStore.getState();
        const state = anonStore.byThread[resolvedThreadId];
        const acknowledged =
          anonStore.acknowledgedUnanonymizedSend[resolvedThreadId] === true;
        const shouldWarn =
          state?.enabled === false &&
          state.everAnonymized === true &&
          !acknowledged;
        if (shouldWarn) {
          const confirmed = await useAnonymizationSendGate
            .getState()
            .request(resolvedThreadId);
          if (!confirmed) {
            throw new DOMException(
              "Send cancelled by anonymisation send-warning modal",
              "AbortError",
            );
          }
          useAnonymizationStore
            .getState()
            .acknowledgeUnanonymizedSend(resolvedThreadId);
        }
      }

      // Read current context at call-time (not captured at adapter-creation time).
      routeContextRef.current.onRouteStateChanged({
        loadingThreadFromUrl: false,
      });

      // Close open PDF documents/panel but preserve citationSnippets —
      // onDocumentCitation will re-accumulate them during this run.
      const pdfStore = usePdfDocumentStore.getState();
      for (const doc of pdfStore.documents) doc.pdfDocumentProxy?.destroy();
      usePdfDocumentStore.setState({
        documents: [],
        activeDocumentId: null,
        panelOpen: false,
        activeCitationSnippet: null,
        citationSnippets: {},
      });

      const parentId =
        messages.length === 1 ? null : messages[messages.length - 2].id;

      // Diagnostic: parentId should be null (first turn) or a backend GUID (after
      // history.load() mapped message entities). Anything else is an assistant-ui
      // local id that the backend can no longer resolve — i.e. the frontend tree is
      // referencing an ephemeral id that was never reconciled with a persisted
      // message. This is the shape of the "Parent message with ID <shortid> not
      // found" incident. The backend now recovers gracefully, but this log is how
      // we diagnose the originating frontend-state bug.
      const GUID_RE =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (parentId !== null && !GUID_RE.test(parentId)) {
        ClientLogger.warn("Non-GUID parentId — unreconciled local id", {
          component: "chatModelAdapter",
          metadata: {
            parentId,
            resolvedThreadId,
            unstable_threadId,
            routeThreadId,
            assistantMessageLocalId: unstable_assistantMessageId,
            messageCount: messages.length,
          },
        });
      }

      // Pull server-assigned attachment ids off the last user message. assistant-ui's
      // AttachmentAdapter.send wrote the praetor GUID into attachment.id when the user
      // pressed Send; pending / errored attachments are filtered out so a half-uploaded
      // file never gets cited in the chat POST. Backend links each id to the new user
      // message and the assembler injects a placeholder marker on this exact turn.
      const lastMessage = messages[messages.length - 1] as unknown as {
        attachments?: Array<{ id: string; status?: { type?: string } }>;
      };
      const attachmentIds =
        lastMessage?.attachments
          ?.filter(
            (a) => a?.status?.type === "complete" && typeof a.id === "string",
          )
          .map((a) => a.id) ?? [];

      // Web search allowlist: when the user has the "Limit to recommended sources"
      // tickbox enabled, send the admin-configured allowlist (currently always
      // empty — see ADR-041). Empty array is intentional and lets the backend
      // render a no-op web search (chip shows a gated lock badge so the user
      // knows it's effectively off). Tickbox off = field omitted (undefined) =
      // unrestricted web search.
      const enableWebSearch = webSearchRef.current;
      const webSearchAllowedDomains =
        enableWebSearch && webSearchLimitToRecommendedRef.current
          ? recommendedSourcesRef.current
          : undefined;

      // v7: no per-turn scope override. "No access" to praetorCases is expressed
      // by deselecting it from the MCP toggle list, so selectedMcpServers already
      // carries the user's intent — no derivation needed here.
      const turnSelectedMcpServers = selectedMcpServersRef.current;

      const stringBody = JSON.stringify({
        threadId: resolvedThreadId,
        message: messages[messages.length - 1],
        assistantMessageLocalId: unstable_assistantMessageId,
        parentId,
        selectedMcpServers: turnSelectedMcpServers,
        extendedThinking: extendedThinkingRef.current,
        enableWebSearch,
        webSearchAllowedDomains,
        provider: selectedProviderRef.current,
        attachmentIds,
        lang: (() => {
          try {
            return sessionStorage.getItem("praetor:lang") ?? "cs";
          } catch {
            return "cs";
          }
        })(),
      });

      const res = await apiAuthFetch(`api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abortSignal,
        body: stringBody,
      });

      if (res.status === 402) {
        appEvents.emit("quota-exceeded");
        throw new Error(i18n.t("error.code.quota_exceeded"));
      }

      if (!res.ok) {
        ClientLogger.error("Chat API request failed", {
          component: "chatModelAdapter",
          metadata: { status: res.status },
        });
        throw new Error(i18n.t("error.code.service_unavailable"));
      }

      // Backend has committed the user-message INSERT + thread UpdatedAt bump
      // synchronously before flushing response headers, so list subscribers can
      // safely re-fetch now and see the active thread at the top.
      if (resolvedThreadId) {
        appEvents.emit("user-message-sent", { threadId: resolvedThreadId });
      }

      // Combine with visibility signal so a thread switch aborts the stream
      // without triggering error UI (the response continues server-side and
      // resumes on switch-back via the existing resume mechanism).
      const visibilitySignal = resolvedThreadId
        ? StreamVisibilityManager.getSignal(resolvedThreadId)
        : undefined;
      const streamSignal = visibilitySignal
        ? AbortSignal.any([abortSignal, visibilitySignal])
        : abortSignal;

      yield* streamChatToRunResults(
        res,
        streamSignal,
        deps,
        onDocumentCitation,
        resolvedThreadId,
        onDocumentReady,
      );
    },
  };
}
